import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ownerApi, inr, monthLabel, errText } from "@/lib/ownerApi";

const blank = (type) => (type === "fixed" ? { label: "Allowance", type, amount: 0 } : { label: "Standard", type, group: "Std 5", percent: 35, share: 100, deduction: 0, months: 11 });
const NUM = ["percent", "share", "deduction", "months", "amount"];

const CompRow = ({ c, groups, onChange, onRemove, tid }) => (
  <div className="grid grid-cols-2 md:grid-cols-7 gap-2 items-end" data-testid={tid}>
    <Input value={c.label} onChange={(e) => onChange({ label: e.target.value })} placeholder="Label" className="md:col-span-2" />
    {c.type === "fixed" ? (
      <label className="text-[11px] text-stone-500 md:col-span-4">Fixed ₹ / month<Input type="number" value={c.amount} onChange={(e) => onChange({ amount: e.target.value })} /></label>
    ) : (<>
      <label className="text-[11px] text-stone-500">Fee group<select className="w-full h-9 rounded-md border border-input bg-white px-2 text-sm" value={c.group} onChange={(e) => onChange({ group: e.target.value })}>{groups.map((g) => <option key={g}>{g}</option>)}</select></label>
      <label className="text-[11px] text-stone-500">%<Input type="number" value={c.percent} onChange={(e) => onChange({ percent: e.target.value })} /></label>
      <label className="text-[11px] text-stone-500">Share %<Input type="number" value={c.share} onChange={(e) => onChange({ share: e.target.value })} /></label>
      <label className="text-[11px] text-stone-500">Less ₹ / ÷ months<div className="flex gap-1"><Input type="number" value={c.deduction} onChange={(e) => onChange({ deduction: e.target.value })} /><Input type="number" className="w-16" value={c.months} onChange={(e) => onChange({ months: e.target.value })} /></div></label>
    </>)}
    <Button variant="ghost" size="sm" onClick={onRemove}><Trash2 className="h-4 w-4 text-red-600" /></Button>
  </div>
);

const TeacherRule = ({ t, groups, months, onSaved }) => {
  const [comps, setComps] = useState(t.current.components);
  const [name, setName] = useState(t.name);
  const [from, setFrom] = useState(months.find((m) => m > t.current.effective_from) || months[months.length - 1]);
  const save = async () => {
    try {
      await ownerApi.put(`/salary/rules/${t.key}`, { name, effective_from: from, active: true, components: comps.map((c) => Object.fromEntries(Object.entries(c).map(([k, v]) => [k, NUM.includes(k) ? Number(v) || 0 : v]))) });
      toast.success(`Saved, applies from ${monthLabel(from)}`); onSaved();
    } catch (e) { toast.error(errText(e)); }
  };
  const upd = (i, p) => setComps(comps.map((c, j) => (j === i ? { ...c, ...p } : c)));
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-4 space-y-3" data-testid={`owner-rule-${t.key}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} className="w-48 font-semibold" data-testid={`owner-rule-name-${t.key}`} />
        <span className="text-xs text-stone-500">Current rule from {monthLabel(t.current.effective_from)} · {t.versions.length} version(s)</span>
      </div>
      {comps.map((c, i) => <CompRow key={i} c={c} groups={groups} tid={`owner-rule-comp-${t.key}-${i}`} onChange={(p) => upd(i, p)} onRemove={() => setComps(comps.filter((_, j) => j !== i))} />)}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => setComps([...comps, blank("percent")])} data-testid={`owner-rule-add-pct-${t.key}`}><Plus className="h-3.5 w-3.5 mr-1" />% of fees</Button>
        <Button size="sm" variant="outline" onClick={() => setComps([...comps, blank("fixed")])} data-testid={`owner-rule-add-fixed-${t.key}`}><Plus className="h-3.5 w-3.5 mr-1" />Fixed / allowance</Button>
        <span className="text-xs text-stone-600 ml-auto">Effective from</span>
        <select className="h-9 rounded-md border border-input bg-white px-2 text-sm" value={from} onChange={(e) => setFrom(e.target.value)} data-testid={`owner-rule-from-${t.key}`}>{months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}</select>
        <Button size="sm" className="bg-emerald-800 hover:bg-emerald-900" onClick={save} data-testid={`owner-rule-save-${t.key}`}>Save new version</Button>
      </div>
    </div>
  );
};

const NewTeacher = ({ onSaved, months }) => {
  const [name, setName] = useState("");
  const add = async () => {
    if (!name.trim()) return;
    try { await ownerApi.put(`/salary/rules/${name}`, { name, effective_from: months[0], active: true, components: [{ label: "Fixed Monthly Salary", type: "fixed", amount: 0 }] }); setName(""); onSaved(); }
    catch (e) { toast.error(errText(e)); }
  };
  return <div className="flex gap-2"><Input placeholder="New teacher name" value={name} onChange={(e) => setName(e.target.value)} className="w-56" data-testid="owner-rule-new-name" /><Button variant="outline" onClick={add} data-testid="owner-rule-new-add">Add teacher</Button></div>;
};

export default function RulesEditor() {
  const [d, setD] = useState(null);
  const load = useCallback(() => ownerApi.get("/salary/rules").then(({ data }) => setD(data)).catch((e) => toast.error(errText(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loader2 className="h-6 w-6 animate-spin text-emerald-800 mx-auto" />;
  return (
    <div className="space-y-4" data-testid="owner-rules">
      <p className="text-sm text-stone-600">Formula for % parts: (Total fees − Less ₹) × % × Share % ÷ months. Saving creates a new version from the chosen month; earlier months and finalised salaries are never changed. To stop a teacher's pay, remove all parts and save from their leaving month. Example total: {inr(100000)} × 35% ÷ 11 = {inr(3182)}.</p>
      {d.teachers.map((t) => <TeacherRule key={`${t.key}-${t.versions.length}`} t={t} groups={d.groups} months={d.salary_months} onSaved={load} />)}
      <NewTeacher months={d.salary_months} onSaved={load} />
    </div>
  );
}
